import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/shared/ui/components/basic/Button';
import { SegmentedControl, SKELETON_BLOCK, skeletonSlots } from '@/shared/ui';
import type { BillingCycle } from '../../hooks/use-plans';

/**
 * Monthly / yearly billing toggle for plan choice.
 *
 * @param props - Cycle value, change handler, and yearly discount label.
 * @returns Segmented billing control.
 */
export const PlanBillingToggle: React.FC<{
  billingCycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
  yearlyDiscountLabel?: string;
}> = ({ billingCycle, onChange, yearlyDiscountLabel = '-30%' }) => {
  const { t } = useTranslation('@pages/onboarding');
  const items = [
    { value: 'monthly' as BillingCycle, label: t('planChoice.frequency.monthly') },
    { value: 'yearly' as BillingCycle, label: `${t('planChoice.frequency.yearly')} ${yearlyDiscountLabel}` },
  ];
  return (
    <SegmentedControl
      value={billingCycle}
      onChange={onChange}
      items={items}
      variant="solid"
      size="sm"
      testId="onboarding-billing-toggle"
    />
  );
};

/** How many placeholder cards stand in for the catalogue. */
const SKELETON_CARDS = 3;

/** Rows of text drawn inside a placeholder card, as fractions of its width. */
const SKELETON_ROWS = ['70%', '45%', '85%', '60%', '80%', '50%'];

/**
 * One placeholder card, drawn in the real card's frame.
 *
 * The frame is the real one — `--surface-raised`, the subtle border, the xl
 * radius — because the skeleton's job is to hold the space the cards will take,
 * and a plain block of colour both moves the layout when the plans land and
 * says nothing about what is coming.
 *
 * @returns A shimmering plan-card placeholder.
 */
const PlanCardSkeleton: React.FC = () => (
  <div
    data-testid="plan-choice-skeleton-card"
    style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
      minHeight: 320, padding: 'var(--space-5)',
      borderRadius: 'var(--radius-xl)', background: 'var(--surface-raised)',
      border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)',
    }}
  >
    <div className={`${SKELETON_BLOCK} h-8 w-8 rounded-(--radius-lg)`} />
    <div className={`${SKELETON_BLOCK} h-5 w-1/2`} />
    <div className={`${SKELETON_BLOCK} h-9 w-2/3`} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      {SKELETON_ROWS.map((width) => (
        <div key={width} className={`${SKELETON_BLOCK} h-3`} style={{ width }} />
      ))}
    </div>
    <div className={`${SKELETON_BLOCK} h-9 w-full rounded-(--radius-lg)`} style={{ marginTop: 'auto' }} />
  </div>
);

/**
 * Loading skeleton for the plan choice grid.
 *
 * It paints itself in `--skeleton-base`, not `--surface-secondary`: that token
 * carries the same value as the page ground the onboarding shell is painted
 * with (`#faf8f5`), so the previous placeholders were three invisible blocks
 * and the step read as a blank screen for the whole of the catalogue fetch.
 *
 * The grid mirrors the real one column for column so the cards land in place
 * rather than pushing the page around when the fetch resolves.
 *
 * @returns Three shimmering placeholder cards.
 */
export const PlanChoiceSkeleton: React.FC = () => (
  <div
    data-testid="plan-choice-skeleton"
    aria-busy="true"
    style={{
      width: '100%', display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 'var(--space-4)', alignItems: 'stretch',
    }}
  >
    {skeletonSlots(SKELETON_CARDS).map((slot) => (
      <PlanCardSkeleton key={slot} />
    ))}
  </div>
);

interface PlanCardCtaProps {
  disabled: boolean;
  isWaitlisted: boolean;
  selected: boolean;
  ctaLabel: string;
  soldOutCta: string;
  selectedLabel: string;
  onClick: (e: React.MouseEvent) => void;
}

/**
 * Plan card primary CTA (select or waitlist notify).
 *
 * @param props - CTA state and click handler.
 * @returns Full-width button.
 */
export const PlanCardCta: React.FC<PlanCardCtaProps> = ({
  disabled,
  isWaitlisted,
  selected,
  ctaLabel,
  soldOutCta,
  selectedLabel,
  onClick,
}) => (
  <Button
    variant={disabled ? 'outlined' : 'solid'}
    size="sm"
    onClick={onClick}
    disabled={disabled && isWaitlisted}
    className={isWaitlisted ? 'animate-[waitlist-pulse_0.6s_ease-out]' : ''}
    style={{
      width: '100%',
      ...(disabled
        ? {
            opacity: 1,
            color: isWaitlisted ? 'var(--surface-primary)' : 'var(--seed-base)',
            borderColor: isWaitlisted ? 'var(--seed-base)' : 'var(--seed-base)',
            background: isWaitlisted ? 'var(--seed-base)' : 'var(--surface-primary)',
            fontWeight: 700,
            cursor: isWaitlisted ? 'default' : 'pointer',
          }
        : {}),
    }}
  >
    {disabled
      ? (isWaitlisted ? `✓ ${soldOutCta}` : soldOutCta)
      : selected ? selectedLabel : ctaLabel}
  </Button>
);
